import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LifeBuoy, CircleDot, Clock, CheckCircle, ChevronDown, ChevronUp, UserCheck, Users, Send, MessageSquare, Sparkles, Loader2 } from "lucide-react";

const STATUS_CONFIG = {
  open:        { label: "Ouvert",   icon: CircleDot,   className: "bg-blue-50 text-blue-600 border-blue-200" },
  in_progress: { label: "En cours", icon: Clock,       className: "bg-orange-50 text-orange-600 border-orange-200" },
  resolved:    { label: "Résolu",   icon: CheckCircle, className: "bg-green-50 text-green-600 border-green-200" },
};

const CATEGORIES = {
  billing:   "Facturation",
  technical: "Technique",
  account:   "Compte",
  feature:   "Suggestion",
  other:     "Autre",
};

function RoleIcon({ role }) {
  return role === 'coach'
    ? <UserCheck className="w-3 h-3" />
    : <Users className="w-3 h-3" />;
}

const PRIORITY_CONFIG = {
  haute:   "bg-red-50 text-red-700 border-red-200",
  moyenne: "bg-orange-50 text-orange-700 border-orange-200",
  basse:   "bg-green-50 text-green-700 border-green-200",
};

function TicketRow({ ticket, onUpdate, refreshBadge }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [replyText, setReplyText] = useState(ticket.admin_reply ?? '');
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  const cfg = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.open;
  const Icon = cfg.icon;

  const handleAnalyzeAI = async (e) => {
    e.stopPropagation();
    setAiLoading(true);
    try {
      const { data } = await api.post('/ai/categorize-ticket', { ticket_id: ticket.ticket_id });
      setAiSuggestion(data);
      if (data.suggested_reply) setReplyText(data.suggested_reply);
      setExpanded(true);
      toast.success("Analyse IA terminée");
    } catch {
      toast.error("Erreur d'analyse IA");
    } finally {
      setAiLoading(false);
    }
  };

  const handleStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/admin/support/${ticket.ticket_id}`, { status: newStatus });
      onUpdate(data);
      refreshBadge?.();
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const { data } = await api.patch(`/admin/support/${ticket.ticket_id}`, { admin_reply: replyText.trim() });
      onUpdate(data);
      refreshBadge?.();
      toast.success("Réponse envoyée");
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      {/* Header row */}
      <button
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
          {ticket.user_name?.[0]?.toUpperCase() ?? '?'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">{ticket.subject}</span>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">
              {CATEGORIES[ticket.category] ?? ticket.category}
            </span>
            {ticket.admin_reply && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 flex-shrink-0">
                <MessageSquare className="w-3 h-3" /> Répondu
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-slate-500">{ticket.user_name}</span>
            <span className="text-xs text-slate-400">·</span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
              <RoleIcon role={ticket.user_role} />
              {ticket.user_role === 'coach' ? 'Coach' : 'Élève'}
            </span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs text-slate-400">
              {new Date(ticket.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {ticket.status !== 'resolved' && (
            <button
              onClick={handleAnalyzeAI}
              disabled={aiLoading}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-purple-200 bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors disabled:opacity-50"
            >
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiLoading ? '...' : 'Analyser IA'}
            </button>
          )}
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.className}`}>
            <Icon className="w-3 h-3" />{cfg.label}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          {/* User message */}
          <div className="px-4 py-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-bold">
                {ticket.user_name?.[0]?.toUpperCase() ?? '?'}
              </div>
              <span className="text-xs font-semibold text-slate-700">{ticket.user_name}</span>
              <span className="text-xs text-slate-400">{ticket.user_email}</span>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 ml-7">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.message}</p>
            </div>
          </div>

          {/* Existing admin reply */}
          {ticket.admin_reply && (
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 mb-2 justify-end">
                <span className="text-xs text-slate-400">{ticket.replied_at ? new Date(ticket.replied_at).toLocaleDateString('fr-FR') : ''}</span>
                <span className="text-xs font-semibold text-blue-700">Admin</span>
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">A</div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mr-7">
                <p className="text-sm text-blue-900 whitespace-pre-wrap">{ticket.admin_reply}</p>
              </div>
            </div>
          )}

          {/* AI Suggestion */}
          {aiSuggestion && (
            <div className="px-4 pb-3">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-semibold text-purple-700">Suggestion IA</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${PRIORITY_CONFIG[aiSuggestion.priority] || ''}`}>
                    Priorité {aiSuggestion.priority}
                  </span>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {CATEGORIES[aiSuggestion.suggested_category] ?? aiSuggestion.suggested_category}
                  </span>
                </div>
                <p className="text-xs text-purple-900">{aiSuggestion.summary}</p>
              </div>
            </div>
          )}

          {/* Reply form */}
          <div className="px-4 pb-4 pt-2 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-2">
              {ticket.admin_reply ? 'Modifier la réponse' : 'Répondre au ticket'}
            </p>
            <div className="flex gap-2 items-end">
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Rédigez votre réponse..."
                rows={3}
                className="flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Button
                size="sm"
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
                className="gap-1.5 flex-shrink-0"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? '...' : 'Envoyer'}
              </Button>
            </div>

            {/* Status change */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-500 font-medium">Statut :</span>
              <Select value={ticket.status} onValueChange={handleStatus} disabled={updating}>
                <SelectTrigger className="h-8 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Ouvert</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="resolved">Résolu</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminSupport() {
  const { refreshBadge } = useOutletContext() ?? {};
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/admin/support${params}`)
      .then(r => setTickets(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = (updated) => {
    setTickets(prev => prev.map(t => t.ticket_id === updated.ticket_id ? updated : t));
  };

  const filters = [
    { value: "",            label: "Tous" },
    { value: "open",        label: "Ouverts" },
    { value: "in_progress", label: "En cours" },
    { value: "resolved",    label: "Résolus" },
  ];

  const openCount = tickets.filter(t => t.status === 'open').length;

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Tickets Support</h1>
        <p className="text-slate-400 mt-1 text-sm">
          {tickets.length} ticket{tickets.length > 1 ? 's' : ''}
          {openCount > 0 && <span className="ml-2 text-orange-600 font-medium">· {openCount} ouvert{openCount > 1 ? 's' : ''}</span>}
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        {filters.map(f => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-4 h-9 rounded-full text-sm font-medium transition-colors border ${
              statusFilter === f.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-4 border border-slate-100 rounded-xl flex gap-3">
              <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-48" />
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <LifeBuoy className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm text-slate-400">Aucun ticket</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map(t => (
            <TicketRow key={t.ticket_id} ticket={t} onUpdate={handleUpdate} refreshBadge={refreshBadge} />
          ))}
        </div>
      )}
    </div>
  );
}
